import { useEffect, useRef } from 'react'
import { color, type State } from './tokens'
import { gsap, EASE_IN, EASE_OUT, prefersReducedMotion } from './gsap'
import { LOOP_BOX, LOOP_W as w, LOOP_H as h, LOOP_R as r, loopPosPct as posPct, pointOnStadium, stadiumMetrics, stadiumPath } from './loop/stadium'

type Stage = Extract<State, 'construcao' | 'conexao' | 'gate' | 'cura'>
const STAGE_COLOR: Record<Stage | 'idle', string> = { idle: color.neutro800, construcao: color.construcao, conexao: color.conexao, gate: color.gate, cura: color.cura }
// Seis paradas contam a suíte inteira numa volta: Genesis constrói, Connect liga, o humano gateia o passo arriscado,
// Auto Care resolve e fecha. Cada parada também monta uma peça da tela do produto atrás do laço (o "construir" tem resultado visível).
const STOPS: { t: number; stage: Stage; reveals: 'frame' | 'header' | 'kpi' | 'card' | 'resolve' | 'nav' }[] = [
  { t: 0, stage: 'construcao', reveals: 'frame' }, { t: 1 / 6, stage: 'construcao', reveals: 'header' }, { t: 2 / 6, stage: 'conexao', reveals: 'kpi' },
  { t: 3 / 6, stage: 'gate', reveals: 'card' }, { t: 4 / 6, stage: 'cura', reveals: 'resolve' }, { t: 5 / 6, stage: 'cura', reveals: 'nav' },
]

export interface HeroLoopCopy {
  /** título da janela do terminal, ex.: "genesis — zsh" */ window: string
  cli: string; workspace: string; spec: string; analyzing: string; done: string
  /** 6 rótulos, na ordem das paradas */ stops: [string, string, string, string, string, string]
  tagGate: string; tagClosed: string
}

/**
 * Herói animado (portado do modelo do Ângelo, 2026-09-06): terminal digita `genesis submit PRODUCT_SPEC.md`, valida a spec
 * e "entrega ao laço"; o laço se desenha parada a parada (Construção → Conexão → Gate → Cura) enquanto a tela do produto
 * se monta atrás; ao fechar, a timeline reinicia do zero — 0% ≡ 100% (ADR-004). Sem sombra, sem alfa em cor de estado,
 * sem gradiente: chapas `fio`/`tinta` com bordas de linha. Reduced-motion: estado final estático no primeiro frame.
 */
export function HeroLoop({ copy, className }: { copy: HeroLoopCopy; className?: string }) {
  const root = useRef<HTMLDivElement>(null)
  const { viewW, viewH, pad } = LOOP_BOX
  const d = stadiumPath(w, h, r)

  useEffect(() => {
    const el = root.current
    if (!el) return
    const q = <T extends Element>(sel: string) => el.querySelector<T>(sel)
    const terminal = q<HTMLElement>('[data-term]'), specEl = q<HTMLElement>('[data-spec]'), cursor = q<HTMLElement>('[data-cursor]'), prompt = q<HTMLElement>('[data-prompt]')
    const analyzing = q<HTMLElement>('[data-analyzing]'), barTrack = q<HTMLElement>('[data-bar]'), barFill = q<HTMLElement>('[data-bar-fill]'), done = q<HTMLElement>('[data-done]')
    const stage = q<HTMLElement>('[data-stage]'), path = q<SVGPathElement>('[data-loop-path]'), label = q<HTMLElement>('[data-label]'), labelText = q<HTMLElement>('[data-label-text]'), labelCheck = q<SVGElement>('[data-label-check]')
    const phone = q<HTMLElement>('[data-phone]'), pHeader = q<HTMLElement>('[data-phone-header]'), pKpi = q<HTMLElement>('[data-phone-kpi]'), pCard = q<HTMLElement>('[data-phone-card]'), pTag = q<HTMLElement>('[data-phone-tag]'), pNav = q<HTMLElement>('[data-phone-nav]')
    const nodes = STOPS.map((_, i) => q<SVGCircleElement>(`[data-node="${i}"]`)), rings = STOPS.map((_, i) => q<SVGCircleElement>(`[data-ring="${i}"]`))
    if (!terminal || !specEl || !cursor || !prompt || !analyzing || !barTrack || !barFill || !done || !stage || !path || !label || !labelText || !labelCheck || !phone || !pHeader || !pKpi || !pCard || !pTag || !pNav || nodes.some((n) => !n) || rings.some((n) => !n)) return
    const { perimeter } = stadiumMetrics(w, r)
    const reveals: Record<string, HTMLElement | null> = { header: pHeader, kpi: pKpi, card: pCard, nav: pNav }

    if (prefersReducedMotion()) {
      specEl.textContent = copy.spec; cursor.style.opacity = '0'
      gsap.set(terminal, { display: 'none' }); gsap.set([stage, phone, pHeader, pKpi, pCard, pNav], { opacity: 1 })
      pTag.textContent = copy.tagClosed; pTag.style.color = STAGE_COLOR.cura
      STOPS.forEach((s, i) => gsap.set(nodes[i], { attr: { fill: STAGE_COLOR[s.stage] }, opacity: 1 }))
      return
    }

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.8 })
      // reset — o mesmo estado do primeiro frame (0% ≡ 100%)
      tl.set(terminal, { opacity: 1, scale: 1, display: 'block' }); tl.set(specEl, { text: '' }); tl.set([prompt, cursor], { opacity: 1 }); tl.set(prompt, { color: color.papel })
      tl.set([analyzing, done, barTrack, stage, phone, pHeader, pKpi, pCard, pNav, label], { opacity: 0 }); tl.set(barFill, { width: '0%' })
      tl.set(path, { strokeDasharray: perimeter, strokeDashoffset: perimeter }); tl.set(nodes, { opacity: 0, attr: { fill: STAGE_COLOR.idle } }); tl.set(rings, { opacity: 0 })
      tl.set(pTag, { textContent: '', color: STAGE_COLOR.gate })
      // terminal: digita, analisa, barra, ok
      tl.to(specEl, { duration: copy.spec.length * 0.032, text: copy.spec, ease: 'none' }, '+=0.4')
      tl.to(cursor, { opacity: 0, repeat: 3, yoyo: true, duration: 0.2 }, '<')
      tl.to(cursor, { opacity: 0, duration: 0.15 }, '+=0.2'); tl.set(prompt, { color: color.neutro400 }, '<') // "apaga" trocando o token, não a opacidade (contraste medido)
      tl.to(analyzing, { opacity: 1, duration: 0.25 }, '+=0.1'); tl.to(barTrack, { opacity: 1, duration: 0.2 }, '<')
      tl.to(barFill, { width: '100%', duration: 1.0, ease: EASE_OUT }, '+=0.05')
      tl.to([analyzing, barTrack], { opacity: 0, duration: 0.2 }, '+=0.05'); tl.to(done, { opacity: 1, duration: 0.25 }, '+=0.05')
      tl.to(terminal, { opacity: 0, scale: 0.94, duration: 0.4, ease: EASE_IN }, '+=0.55'); tl.set(terminal, { display: 'none' })
      // laço + tela do produto
      tl.to([stage, phone], { opacity: 1, duration: 0.2 }, '<0.1')
      const showLabel = (i: number) => {
        const pos = posPct(STOPS[i].t)
        labelText.textContent = copy.stops[i]; labelText.style.color = STAGE_COLOR[STOPS[i].stage]
        labelCheck.style.display = STOPS[i].stage === 'gate' ? 'block' : 'none'
        gsap.set(label, { left: `${pos.left}%`, top: `${pos.top}%`, xPercent: pos.left < 22 ? -12 : pos.left > 78 ? -88 : -50, yPercent: pos.top < 18 ? 30 : -170 })
      }
      STOPS.forEach((stop, i) => {
        const drawTo = perimeter * (1 - (i + 1) / STOPS.length)
        if (i === 0) {
          tl.call(() => showLabel(0)); tl.to(label, { opacity: 1, duration: 0.25 }, '<')
          tl.to(nodes[0], { opacity: 1, attr: { fill: STAGE_COLOR[stop.stage] }, duration: 0.2 }, '<'); tl.to(rings[0], { opacity: 1, duration: 0.2 }, '<')
        } else {
          tl.to(label, { opacity: 0, duration: 0.15 }, '+=0.35')
          tl.to(path, { strokeDashoffset: drawTo, duration: 0.35, ease: EASE_OUT }, '<')
          tl.to(nodes[i], { opacity: 1, attr: { fill: STAGE_COLOR[stop.stage] }, duration: 0.2 }, '>-0.1'); tl.to(rings[i], { opacity: 1, duration: 0.2 }, '<')
          const rv = reveals[stop.reveals]; if (rv) tl.to(rv, { opacity: 1, duration: 0.3 }, '<')
          if (stop.reveals === 'resolve') tl.call(() => { pTag.textContent = copy.tagClosed; pTag.style.color = STAGE_COLOR.cura })
          tl.call(() => showLabel(i)); tl.to(label, { opacity: 1, duration: 0.25 }, '+=0.05')
        }
        if (stop.stage === 'gate') { tl.call(() => { pTag.textContent = copy.tagGate; pTag.style.color = STAGE_COLOR.gate }); tl.to({}, { duration: 0.6 }) }
      })
      tl.to(label, { opacity: 0, duration: 0.15 }, '+=0.4'); tl.to(path, { strokeDashoffset: 0, duration: 0.3, ease: EASE_OUT }, '<')
      tl.to({}, { duration: 1.8 }); tl.to([stage, phone], { opacity: 0, duration: 0.3 }, '+=0.1')
    }, el)
    return () => ctx.revert()
  }, [copy])

  return (
    <div ref={root} className={['z-heroloop', className].filter(Boolean).join(' ')} aria-hidden="true">
      <div data-term className="z-term">
        <div className="z-term__barra"><span /><span /><span /><em>{copy.window}</em></div>
        <div className="z-term__corpo">
          <p className="z-term__dim">{copy.cli}</p>
          <p className="z-term__dim">{copy.workspace}</p>
          <p data-prompt className="z-term__prompt"><span data-spec /><span data-cursor className="z-term__cursor" /></p>
          <p data-analyzing className="z-term__dim" style={{ opacity: 0 }}>{copy.analyzing}</p>
          <div data-bar className="z-term__bar" style={{ opacity: 0 }}><div data-bar-fill className="z-term__bar-fill" /></div>
          <p data-done className="z-term__ok" style={{ opacity: 0 }}>{copy.done}</p>
        </div>
      </div>
      <div data-stage className="z-heroloop__palco" style={{ opacity: 0 }}>
        <div data-phone className="z-phone" style={{ opacity: 0 }}>
          <div className="z-phone__notch" />
          <div data-phone-header className="z-phone__header" style={{ opacity: 0 }}><span className="z-phone__app" /><span className="z-phone__linha z-phone__linha--m" /></div>
          <div data-phone-kpi className="z-phone__kpis" style={{ opacity: 0 }}>
            <div className="z-phone__kpi"><span className="z-phone__linha z-phone__linha--cura" /><span className="z-phone__linha z-phone__linha--s" /></div>
            <div className="z-phone__kpi"><span className="z-phone__linha z-phone__linha--conexao" /><span className="z-phone__linha z-phone__linha--s" /></div>
          </div>
          <div data-phone-card className="z-phone__card" style={{ opacity: 0 }}>
            <div className="z-phone__card-topo"><span className="z-phone__linha z-phone__linha--s" /><span data-phone-tag className="z-phone__tag" /></div>
            <span className="z-phone__linha z-phone__linha--l" /><span className="z-phone__linha z-phone__linha--m z-phone__linha--fraca" />
          </div>
          <div data-phone-nav className="z-phone__nav" style={{ opacity: 0 }}><span /><span /><span /><span /></div>
        </div>
        <svg viewBox={`0 0 ${viewW} ${viewH}`} className="z-loop" role="img">
          <g transform={`translate(${pad},${pad})`}>
            <path data-loop-path d={d} fill="none" className="z-loop__traco" strokeWidth={2} />
            {STOPS.map((stop, i) => { const { x, y } = pointOnStadium(stop.t, w, h, r); return (
              <g key={i} data-role="no">
                <circle data-ring={i} cx={x} cy={y} r={14} fill="none" stroke={STAGE_COLOR[stop.stage]} strokeWidth={1.5} opacity={0} />
                <circle data-node={i} cx={x} cy={y} r={9} fill={STAGE_COLOR.idle} opacity={0} />
              </g>) })}
          </g>
        </svg>
        <div data-label className="z-heroloop__rotulo" style={{ opacity: 0 }}>
          <svg data-label-check width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ display: 'none' }}><path d="M2.5 6.2 L5 8.7 L9.5 3.3" stroke={color.gate} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          <span data-label-text />
        </div>
      </div>
    </div>
  )
}
